import { ReactElement, useMemo } from "react"

import { Toggle } from "@wfp/ui"

import { faCheckCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { IReportObs } from "../../../../../interfaces/markets"
import { defaultOutlierFilters } from "../../../../../constants/validation"

import Slider from "../../../../Shared/Slider/Slider"

type Props = {
  filters: { MoM: number; YoY: number; showOutliers: boolean }
  setFilters: (filters: { MoM: number; YoY: number; showOutliers: boolean }) => void
  data: IReportObs[]
}

const OutlierFilters = ({ filters, setFilters, data }: Props): ReactElement => {
  const outliersCount = useMemo(() => {
    if (!data?.length) return undefined

    return data?.filter(item => {
      if (item.isOutlier) return true
      if (item.MoM !== undefined && Math.abs(item.MoM ?? 0) >= filters.MoM) return true
      return item.YoY !== undefined && Math.abs(item.YoY ?? 0) >= filters.YoY
    })?.length
  }, [data, filters.MoM, filters.YoY])

  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center w-full gap-1 xl:gap-x-10">
      <div className="flex items-center gap-1 min-w-[200px]">
        {outliersCount === 0 ? (
          <>
            <FontAwesomeIcon icon={faCheckCircle} color="green" size="sm" className="mr-0.5" />
            <span className="text-xs font-medium text-green-800">All prices validated - no outliers detected</span>
          </>
        ) : (
          !!outliersCount && (
            <>
              <span className="text-xs font-medium text-gray-500">Price outliers detected:</span>
              <span className="text-xs font-bold text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded">
                {outliersCount}
              </span>
            </>
          )
        )}
      </div>

      <div className="flex flex-col xl:flex-row justify-self-center gap-1 xl:gap-6 min-w-[280px]">
        <Slider
          value={filters.MoM}
          onChange={value => setFilters({ ...filters, MoM: value })}
          min={0}
          max={200}
          color="var(--interactive-01)"
          label="MoM threshold"
          className="2xl:min-w-[280px]"
          defaultValue={defaultOutlierFilters?.MoM}
        />
        <Slider
          value={filters.YoY}
          onChange={value => setFilters({ ...filters, YoY: value })}
          min={0}
          max={200}
          color="var(--interactive-01)"
          label="YoY threshold"
          className="2xl:min-w-[280px]"
          defaultValue={defaultOutlierFilters?.YoY}
        />
      </div>

      <div className="lg:place-content-end">
        <Toggle
          labelA="Show only outliers"
          labelB=""
          onToggle={() => setFilters({ ...filters, showOutliers: !filters.showOutliers })}
          toggled={filters.showOutliers}
          className="outlier-toggle"
        />
      </div>
    </div>
  )
}

export default OutlierFilters
